import {
  type MarketingMedia,
  approvedImage,
  approvedVideo,
} from '../marketing-media';
import { comicPerformanceBenchmark } from './ComicPerformanceChart/benchmark.ts';

export type ModelMedia = MarketingMedia;

export type ModelActionTarget =
  | 'api'
  | 'mcp'
  | 'capture-app'
  | 'editor-app'
  | 'generation-app';

export type ModelLearnTarget =
  | '/product/performance-capture'
  | '/product/performance-editor'
  | '/product/performance-generation'
  | '#comic-performance'
  | 'contact';

const comicCapabilityClips: Record<string, string> = {
  'multi-subject': '02_comic_capabilities_multi-subject.webm',
  'accurate-subject-scale': '03_comic_capabilities_accurate-subject-scale.webm',
  faces: '04_comic_capabilities_faces.webm',
  'hands-wrists-fingers': '05_comic_capabilities_hands-wrists-fingers.webm',
  'camera-intrinsics-extrinsics':
    '06_comic_capabilities_camera_intrinsics-extrinsics.mp4',
  'foot-ground-contact': '07_comic_capabilities_foot-ground-contact.webm',
  'accurate-world-space-root-translation-and-height':
    '08_comic_capabilities_world_space.webm',
  objects: 'comic_objects_output.webm',
  environments: '10_comic_capabilities_environments.webm',
};

const swingCapabilityClips: Record<string, string> = {
  'text-to-motion': '02_swing_capabilities_text-to-motion.webm',
  'pose-to-motion': '03_swing_capabilities_pose-to-motion.webm',
  'duration-adjustment': '04_swing_capabilities_definable_length.webm',
  'batch-generation': '05_swing_capabilities_batch-generation.webm',
  'auto-looping': '06_swing_capabilities_auto-looping.webm',
  'curve-smoothing': '07_swing_capabilities_cirve-smoothing.webm',
  'multi-subject': '08_swing_capabilities_multi-subject.webm',
  hands: '10_swing_capabilities_hands.webm',
  faces: 'swing_faces_output.webm',
  objects: 'swing_objects_output.webm',
};

function capabilityMedia(
  clips: Record<string, string>,
  id: string,
  title: string,
): ModelMedia {
  const description = `${title} capability demonstration.`;
  const filename = clips[id];
  if (!filename) return { type: 'empty', description };
  return approvedVideo(filename, description);
}

interface ModelCardBase {
  description: string;
  id: string;
  title: string;
}

export type ModelCardContent = ModelCardBase &
  (
    | { action: 'try'; actionLabel: string; actionTarget: ModelActionTarget }
    | { action: 'learn'; actionLabel: string; actionTarget: ModelLearnTarget }
    | { action: 'status'; actionLabel: string }
  );

export interface CapabilityContent {
  id: string;
  inResearch?: boolean;
  media: ModelMedia;
  title: string;
}

export interface ModelIntroContent {
  label: string;
  media: ModelMedia;
  paragraphs: readonly [string, string, string];
  title: string;
  tryLabel: string;
  tryTarget: ModelActionTarget;
}

export type ComicPerformanceMetricId =
  | 'pose-accuracy'
  | 'motion-fidelity'
  | 'world-accuracy'
  | 'trajectory'
  | 'root-drift'
  | 'smoothness'
  | 'foot-contact'
  | 'speed';

export interface ComicPerformanceMetric {
  description: string;
  id: ComicPerformanceMetricId;
  label: string;
  lowerIsBetter: boolean;
  shortDescription: string;
  unit: string;
}

export interface ComicPerformanceSeries {
  id: string;
  label: string;
  rawValues: Record<ComicPerformanceMetricId, number>;
  scores: Record<ComicPerformanceMetricId, number>;
  sourceId: string;
}

export interface ComicPerformanceContent {
  accessibilityDisclaimer: string;
  benchmark: typeof comicPerformanceBenchmark.metadata;
  disclaimer: string;
  metrics: ComicPerformanceMetric[];
  models: ComicPerformanceSeries[];
}

export const commitContent = {
  intro: {
    label: 'Comic',
    title:
      'Comic is our family of motion perception and quantification models.',
    paragraphs: [
      'Comic captures human motion from monocular video, estimating not just depth, pose, scale and camera position, but the physics beneath the motion — gravity, ground contact, and the forces that yield position, velocity, and acceleration — to deliver industry-leading performance.',
      'Comic captures production-grade motion directly from video, at a fraction of legacy cost — collapsing the tradeoff between quality and scale, and making it viable to generate motion data in the volumes physical AI requires.',
      'Our customers use it for gameplay and narrative animation, robotic motion authoring, expert trajectory data, AI model training data, neural rendering, sports performance, and clinical movement analysis.',
    ],
    tryLabel: 'Try Comic',
    tryTarget: 'capture-app',
    media: approvedVideo(
      '02_homepage_our-models_comic.webm',
      'Comic model motion preview.',
    ),
  },
  models: [
    {
      id: 'comic-4',
      title: 'Comic 4',
      description:
        'Multi-subject, faces, scale, and camera capture in the highest fidelity',
      action: 'try',
      actionLabel: 'Try Comic 4',
      actionTarget: 'capture-app',
    },
    {
      id: 'comic-3',
      title: 'Comic 3',
      description: 'Fast, single-subject body performance capture.',
      action: 'try',
      actionLabel: 'Try Comic 3',
      actionTarget: 'capture-app',
    },
    {
      id: 'comic-realtime',
      title: 'Comic Realtime',
      description:
        'Realtime motion capture & retargeting for streaming or recording',
      action: 'status',
      actionLabel: 'In Research',
    },
  ],
  capabilities: (
    [
      ['multi-subject', 'Multi-subject'],
      ['accurate-subject-scale', 'Accurate subject scale'],
      ['faces', 'Faces'],
      ['hands-wrists-fingers', 'Hands, wrists, & fingers'],
      ['camera-intrinsics-extrinsics', 'Camera intrinsics & extrinsics'],
      ['foot-ground-contact', 'Foot/ground contact'],
      [
        'accurate-world-space-root-translation-and-height',
        'Accurate world space, root translation, and height',
      ],
      ['objects', 'Objects'],
      ['environments', 'Environments'],
    ] as const
  ).map(([id, title]) => ({
    id,
    title,
    inResearch: title === 'Objects' || title === 'Environments',
    media: capabilityMedia(comicCapabilityClips, id, title),
  })),
  performance: {
    disclaimer:
      'Historical EMDB benchmark · September 4, 2026 · higher normalized score is better.',
    accessibilityDisclaimer:
      'Historical EMDB benchmark data. Raw error measures are lower-is-better; their supplied normalized scores invert errors so higher is better. Results predate later MHR audit corrections.',
    benchmark: comicPerformanceBenchmark.metadata,
    metrics: [
      {
        id: 'pose-accuracy',
        label: 'Pose accuracy',
        shortDescription: 'PA-MPJPE · mm',
        description: 'Raw benchmark error. Lower is better.',
        unit: 'mm',
        lowerIsBetter: true,
      },
      {
        id: 'motion-fidelity',
        label: 'Motion fidelity',
        shortDescription: 'accel error · m/s²',
        description: 'Raw benchmark error. Lower is better.',
        unit: 'm/s²',
        lowerIsBetter: true,
      },
      {
        id: 'world-accuracy',
        label: 'World accuracy',
        shortDescription: 'WA-MPJPE · mm',
        description: 'Raw benchmark error. Lower is better.',
        unit: 'mm',
        lowerIsBetter: true,
      },
      {
        id: 'trajectory',
        label: 'Trajectory',
        shortDescription: 'W-MPJPE · mm',
        description: 'Raw benchmark error. Lower is better.',
        unit: 'mm',
        lowerIsBetter: true,
      },
      {
        id: 'root-drift',
        label: 'Root drift',
        shortDescription: 'RTE · % of path',
        description: 'Raw benchmark error. Lower is better.',
        unit: '% of path',
        lowerIsBetter: true,
      },
      {
        id: 'smoothness',
        label: 'Smoothness',
        shortDescription: 'jitter · 10 m/s³',
        description: 'Raw benchmark error. Lower is better.',
        unit: '10 m/s³',
        lowerIsBetter: true,
      },
      {
        id: 'foot-contact',
        label: 'Foot contact',
        shortDescription: 'foot skate · mm',
        description: 'Raw benchmark error. Lower is better.',
        unit: 'mm',
        lowerIsBetter: true,
      },
      {
        id: 'speed',
        label: 'Speed',
        shortDescription: '× realtime (L40S)',
        description: 'Raw benchmark throughput. Higher is better.',
        unit: '× realtime',
        lowerIsBetter: false,
      },
    ],
    models: comicPerformanceBenchmark.models,
  },
  action: {
    title: 'See Comic in action',
    description:
      'Try Comic through the Cartwheel MCP or in the Performance Capture app.',
    cards: [
      {
        id: 'comic-mcp',
        title: 'Cartwheel MCP',
        action: 'Try the MCP',
        target: 'mcp',
        media: approvedImage(
          '11_comic_see-comic-in-action_mcp.png',
          'Cartwheel MCP integrations with AI assistants.',
        ),
      },
      {
        id: 'comic-performance-capture',
        title: 'Comic in Performance Capture',
        action: 'Try the app',
        target: 'capture-app',
        media: approvedVideo(
          '12_comic_see-comic-in-action_performance-capture.webm',
          'Comic performance capture demonstration.',
        ),
      },
    ],
  },
} satisfies {
  action: {
    cards: {
      action: string;
      id: string;
      media: ModelMedia;
      title: string;
      target: ModelActionTarget;
    }[];
    description: string;
    title: string;
  };
  capabilities: CapabilityContent[];
  intro: ModelIntroContent;
  models: ModelCardContent[];
  performance: ComicPerformanceContent;
};

export const swingContent = {
  intro: {
    label: 'Swing',
    title: 'Swing is our family of generative human motion models',
    paragraphs: [
      'Swing generates human motion from text and poses. Describe the action, intent, style, timing, looping, and number of performances, and Swing produces them rapidly and at scale. Trained on the largest, highest-quality motion dataset ever assembled, Swing brings real-world knowledge to the motion it creates.',
      'By generating smooth motion in seconds, Swing produces motion datasets at scale, far faster and more cheaply than ever before.',
      'For teams needing more specificity than a general model can offer, we build custom versions of Swing tuned to a single character, a style of movement like a kung-fu fighter or a wounded soldier, or a whole cast that shares a common language of motion.',
    ],
    tryLabel: 'Try Swing',
    tryTarget: 'generation-app',
    media: approvedVideo(
      '03_homepage_our-models_swing.webm',
      'Swing model motion preview.',
    ),
  },
  models: [
    {
      id: 'swing-sketch',
      title: 'Swing Sketch',
      description: 'Our flagship generative model for human motion',
      action: 'try',
      actionLabel: 'Try in Performance Generator',
      actionTarget: 'generation-app',
    },
    {
      id: 'swing-edit',
      title: 'Swing Edit',
      description: 'Optimized for crafting existing motion generatively',
      action: 'try',
      actionLabel: 'Try in Performance Editor',
      actionTarget: 'editor-app',
    },
    {
      id: 'swing-choreo',
      title: 'Swing Choreo',
      description:
        'Faces, fingers, multi-character and precise performance direction',
      action: 'try',
      actionLabel: 'Try in Performance Generator',
      actionTarget: 'generation-app',
    },
    {
      id: 'swing-interactive',
      title: 'Swing Interactive',
      description: 'Parametric motion for runtime systems',
      action: 'status',
      actionLabel: 'In Research',
    },
  ],
  capabilities: (
    [
      ['text-to-motion', 'Text-to-motion'],
      ['pose-to-motion', 'Pose-to-motion'],
      ['duration-adjustment', 'Definable control'],
      ['batch-generation', 'Batch generation'],
      ['auto-looping', 'Auto looping'],
      ['curve-smoothing', 'Curve smoothing'],
      ['multi-subject', 'Multi-subject'],
      ['hands', 'Hands'],
      ['faces', 'Faces'],
      ['objects', 'Objects'],
    ] as const
  ).map(([id, title]) => ({
    id,
    title,
    inResearch: title === 'Objects',
    media: capabilityMedia(swingCapabilityClips, id, title),
  })),
  action: {
    title: 'See Swing in action',
    description:
      'Try Swing through the Cartwheel API or in the Motion Generation or Motion Editor apps.',
    cards: [
      {
        id: 'swing-mcp',
        title: 'Cartwheel MCP',
        action: 'Try the MCP',
        target: 'mcp',
        media: approvedImage(
          '13_swing_see-swing-in-action_mcp.png',
          'Cartwheel MCP integrations with AI assistants.',
        ),
      },
      {
        id: 'motion-generation',
        title: 'Try Performance Generation',
        action: 'Try the app',
        target: 'generation-app',
        media: approvedVideo(
          '14_swing_see-swing-in-action_performance-generation.webm',
          'Swing in Performance Generation demonstration.',
        ),
      },
      {
        id: 'motion-editor',
        title: 'Try Performance Editor',
        action: 'Try the app',
        target: 'editor-app',
        media: approvedVideo(
          '15_swing_see-swing-in-action_performance-editor.webm',
          'Swing in Performance Editor demonstration.',
        ),
      },
    ],
    custom: {
      title: 'Train your own',
      description:
        'Work with us to build your custom models for analysis, style, character consistency and more',
      action: 'Discuss a project',
    },
  },
} satisfies {
  action: {
    cards: {
      action: string;
      id: string;
      media: ModelMedia;
      title: string;
      target: ModelActionTarget;
    }[];
    custom: { action: string; description: string; title: string };
    description: string;
    title: string;
  };
  capabilities: CapabilityContent[];
  intro: ModelIntroContent;
  models: ModelCardContent[];
};
